import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from "@/components/ui/PaginationWrapper";
import RequestGroupNumber from './RequestGroupNumber';
import { User, Pencil, RotateCcw, Globe, Copy, Send, Info, Loader2, Smartphone } from 'lucide-react';
import copy from "copy-to-clipboard";
import { userService } from '../../_services';

function ManageGroupNumber({
    text,
    closePopup,
    openResetGroup,
    openAssignGroup,
    openEditGroup,
    openCampaign,
    selectedGroup,
    mGroupPagin,
    mGroupSearch: initialSearch,
    confirmText,
    reopenFlag: initialReopenFlag,
    allData: initialAllData
}) {
    const queryClient = useQueryClient();
    const [saveLoader, setSaveLoader] = useState(false);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [error, setError] = useState('');
    const [activePage, setActivePage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [currentPaging, setCurrentPaging] = useState(1);
    const [dataNotFound, setDataNotFound] = useState('');
    const [copyTooltip, setCopyTooltip] = useState({});
    const [copyTooltipPortal, setCopyTooltipPortal] = useState({});
    const [copyTooltipOpen, setCopyTooltipOpen] = useState({});
    const [copyTooltipPortalOpen, setCopyTooltipPortalOpen] = useState({});
    const [isResetDone, setIsResetDone] = useState(false);
    const [resendMessage, setResendMessage] = useState('Resend Email');
    const [resendMessageBroker, setResendMessageBroker] = useState('Resend Credentials');
    const [reopenFlag, setReopenFlag] = useState(initialReopenFlag || 'direct');
    const [hideButton, setHideButton] = useState(false);
    const [toggleRequestGroupPopup, setToggleRequestGroupPopup] = useState(false);
    const [loadingStates, setLoadingStates] = useState({});
    const [isMobile, setIsMobile] = useState(false);

    const searchInputRef = useRef(null);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback(async (response) => {
        try {
            const text = await response.text();
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    }, []);

    // React Query: Fetch user list with automatic caching
    const { 
        data: apiData, 
        isLoading: loading, 
        error: queryError,
        refetch: refetchUserList 
    } = useQuery({
        // Query key without reopenFlag to ensure consistent cache invalidation
        queryKey: ['manageGroupUsers', userdata?.wholesaler_Id, userdata?.userrole],
        queryFn: async () => {
            const endpoint = reopenFlag === 'Override' ? '/api/ws/whusers_override' : '/api/ws/whusers';
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    roleName: userdata.paramsname,
                    wholesaler_Id: userdata.wholesaler_Id,
                    roleType: userdata.userrole,
                    searchtText: ''
                })
            });

            const apidata = await handleResponse(response);
            return apidata || [];
        },
        enabled: !!userdata,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        refetchOnMount: true, // Refetch if cache is stale (invalidated after save)
        refetchOnWindowFocus: false, // Don't refetch on window focus
        onSuccess: (data) => {
            // Data fetched successfully
        },
        onError: (error) => {
            setError('Error loading data');
        }
    });


    const handlePageChange = useCallback((pageNumber) => {
        const userListCopy = [...userList];
        const usertemplistobj = userListCopy.length > 10 ? userListCopy.slice((pageNumber - 1) * 10, pageNumber * 10) : userListCopy;
        setActivePage(pageNumber);
        setUserListTemp(usertemplistobj);
        setCurrentPaging(pageNumber);
    }, [userList]);

    const filterRecords = useCallback((searchText, mainData) => {
        const lowerSearch = searchText.toLowerCase();
        const upperSearch = searchText.toUpperCase();
        
        const filtered = mainData.filter(e1 => 
            e1.distributorName?.toLowerCase().includes(lowerSearch) || 
            e1.groupNumber?.includes(upperSearch) || 
            e1.emailId?.toLowerCase().includes(lowerSearch)
        );
        
        return filtered;
    }, []);

    // Process API data when it changes
    useEffect(() => {
        if (!apiData) return;
        
        if (apiData.length > 0) {
            const usertemplistobj = apiData.length > 10 ? apiData.slice(0, 10) : apiData;
            setUserList(apiData);
            setMainAPIResult(apiData);
            setUserListTemp(usertemplistobj);
            setError('');
            setDataNotFound('');
            
            // Handle pagination from props
            if (mGroupPagin > 1) {
                const tempList = apiData.length > 10 ? apiData.slice((mGroupPagin - 1) * 10, mGroupPagin * 10) : apiData;
                setActivePage(mGroupPagin);
                setUserListTemp(tempList);
                setCurrentPaging(mGroupPagin);
            }
            
            // Handle initial search from props
            if (initialSearch && !userService.Check_Null_Empty(initialSearch)) {
                setSearchValue(initialSearch);
                const filtered = filterRecords(initialSearch, apiData);
                const filteredTemp = filtered.length > 10 ? filtered.slice(0, 10) : filtered;
                setUserList(filtered);
                setUserListTemp(filteredTemp);
                setHideButton(true);
            }
            
            // Handle reset confirmation
            if (confirmText === 'yes' && !isResetDone) {
                removeSelectedReps('0', selectedGroup, userdata.wholesaler_Id.toString());
            }
        } else {
            setUserList([]);
            setUserListTemp([]);
            setDataNotFound('No records found');
        }
    }, [apiData, mGroupPagin, initialSearch, confirmText, isResetDone, selectedGroup, filterRecords]);

    const removeSelectedReps = useCallback(async (UserId, GroupNumber, WhName) => {
        try {
            const response = await fetch('/api/ws/RemoveReps', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ UserId, GroupNumber, WhName })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].code !== "-1") {
                setError('Record reset successfully');
                setIsResetDone(true);
                setTimeout(() => {
                    // Invalidate cache and refetch
                    queryClient.invalidateQueries(['manageGroupUsers']);
                    setSearchValue('');
                }, 1000);
            } else {
                setError('Error. Please try later.');
                setIsResetDone(true);
            }
        } catch (err) {
            setError('Error. Please try later.');
            setIsResetDone(true);
        }
    }, [handleResponse, queryClient]);

    // Initial setup for override mode
    useEffect(() => {
        if (initialReopenFlag === 'Override' && initialAllData) {
            setMainAPIResult(initialAllData);
            if (initialSearch) {
                setSearchValue(initialSearch);
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearchChange = useCallback((e) => {
        const searchText = e.target.value;
        setSearchValue(searchText);
        
        const mainData = reopenFlag === 'Override' ? initialAllData : mainAPIResult;
        
        if (searchText.length > 0) {
            setHideButton(true);
            const filtered = filterRecords(searchText, mainData);
            
            if (filtered.length === 0) {
                setDataNotFound('No records found');
                } else {
                setDataNotFound('');
                }
            
            const filteredTemp = filtered.length > 10 ? filtered.slice(0, 10) : filtered;
            setActivePage(1);
            setUserList(filtered);
            setUserListTemp(filteredTemp);
            } else {
            setHideButton(false);
            const usertemplistobj = mainData.length > 10 ? mainData.slice(0, 10) : mainData;
            setActivePage(1);
            setUserList(mainData);
            setUserListTemp(usertemplistobj);
            setDataNotFound('');
        }
    }, [reopenFlag, initialAllData, mainAPIResult, filterRecords]);

    const handleSearchOverrides = useCallback(() => {
        setReopenFlag('Override');
        setSaveLoader(true);
        // Invalidate cache and force refetch with Override endpoint
        queryClient.invalidateQueries({
            queryKey: ['manageGroupUsers'],
            refetchType: 'active'
        }).then(() => {
            setSaveLoader(false);
        });
    }, [queryClient]);

    const handleResetGroupClick = useCallback((groupNumber, topCommission) => {
        closePopup?.(groupNumber, topCommission, currentPaging, searchValue, 'ResetGroup');
        openResetGroup?.('Are you sure want to reset details for this group number?');
    }, [closePopup, openResetGroup, currentPaging, searchValue]);

    const handleAssignClick = useCallback((groupNumber, topCommission, isNewUser) => {
        closePopup?.(groupNumber, topCommission, currentPaging, searchValue, '', isNewUser, reopenFlag, mainAPIResult);
        openAssignGroup?.(groupNumber);
    }, [closePopup, openAssignGroup, currentPaging, searchValue, reopenFlag, mainAPIResult]);

    const handleEditGroupClick = useCallback((groupNumber, topCommission, userType, isNewUser) => {
        closePopup?.(groupNumber, topCommission, currentPaging, searchValue, '', isNewUser, reopenFlag, mainAPIResult);
        openEditGroup?.('', '', '', '', userType, groupNumber);
    }, [closePopup, openEditGroup, currentPaging, searchValue, reopenFlag, mainAPIResult]);

    const handleEditCampaignClick = useCallback((groupNumber, topCommission, userType, email, isNewUser) => {
        closePopup?.(groupNumber, topCommission, currentPaging, searchValue, '', isNewUser, reopenFlag, mainAPIResult);
        openCampaign?.('', '', '', '', userType, email);
    }, [closePopup, openCampaign, currentPaging, searchValue, reopenFlag, mainAPIResult]);

    const handleResendEmail = useCallback(async (email, groupNumber, rowIndex) => {
        setLoadingStates(prev => ({ ...prev, [`resend_${rowIndex}`]: true }));
        
        try {
            const response = await fetch('/api/ws/dlinkmailtrigger', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Uemail: email, Uname: groupNumber })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0) {
                const message = apidata[0].message || 'Email sent successfully';
                setResendMessage(message);
                setError(message);
                setTimeout(() => {
                    setResendMessage('Resend Email');
                    setError('');
                }, 3000);
            } else {
                setResendMessage('Error. Please try later.');
                setError('Error. Please try later.');
                setTimeout(() => {
                    setResendMessage('Resend Email');
                    setError('');
                }, 3000);
            }
        } catch (err) {
            setResendMessage('Error. Please try later.');
            setError('Error. Please try later.');
            setTimeout(() => {
                setResendMessage('Resend Email');
                setError('');
            }, 3000);
        } finally {
            setLoadingStates(prev => ({ ...prev, [`resend_${rowIndex}`]: false }));
        }
    }, [handleResponse]);

    const handleResendEmailBroker = useCallback(async (email, groupNumber, rowIndex) => {
        setLoadingStates(prev => ({ ...prev, [`resendBroker_${rowIndex}`]: true }));
        
        try {
            const response = await fetch('/api/broker/reset_credential', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Uemail: email, Uname: groupNumber })
            });

            const apidata = await handleResponse(response);
            
            if (apidata?.length > 0 && apidata[0].code === 200 && apidata[0].success) {
                setError(apidata[0].detail?.[0]?.messagedetail || 'Success');
                setTimeout(() => setError(''), 3000);
                        } else {
                setError(apidata?.[0]?.detail?.[0]?.messagedetail || 'Error. Please try later.');
                setTimeout(() => setError(''), 3000);
            }
        } catch (err) {
            setError('Error. Please try later.');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoadingStates(prev => ({ ...prev, [`resendBroker_${rowIndex}`]: false }));
        }
    }, [handleResponse]);

    const handleCopyDeeplink = useCallback((deeplink, index) => {
        copy(deeplink);
        setCopyTooltipOpen(prev => ({ ...prev, [index]: true }));
        setCopyTooltip(prev => ({ ...prev, [index]: 'Copied' }));
        setTimeout(() => {
            setCopyTooltip(prev => ({ ...prev, [index]: 'Deeplink-Click to Copy' }));
            setCopyTooltipOpen(prev => ({ ...prev, [index]: false }));
        }, 1000);
    }, []);

    const handleCopyPortalLink = useCallback((portalLink, index) => {
        copy(portalLink);
        setCopyTooltipPortalOpen(prev => ({ ...prev, [index]: true }));
        setCopyTooltipPortal(prev => ({ ...prev, [index]: 'Copied' }));
        setTimeout(() => {
            setCopyTooltipPortal(prev => ({ ...prev, [index]: 'Enrollment Center - Click to Copy' }));
            setCopyTooltipPortalOpen(prev => ({ ...prev, [index]: false }));
        }, 1000);
    }, []);

    const handleSpecialCase = useCallback(() => {
        setError('This action is not available');
        setTimeout(() => setError(''), 3000);
    }, []);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup?.();
        }
    }, [closePopup]);

    const toggleRequestGroup = useCallback(() => {
        setToggleRequestGroupPopup(prev => !prev);
    }, []);

        return (
        <TooltipProvider>
            <Dialog open={true} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-4xl max-h-[90vh] [&>div]:!p-0">
                    <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6">
                        <DialogTitle>{text}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : (
                    <div className="space-y-4 pb-4 sm:pb-6">
                        {/* Search Section */}
                        <div className="flex items-center gap-2 px-4 sm:px-6">
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search by name, group number, or email..."
                                value={searchValue}
                                onChange={handleSearchChange}
                                disabled={loading}
                                className="flex-1"
                            />
                            {hideButton && (
                                <Button 
                                    onClick={handleSearchOverrides}
                                    disabled={saveLoader}
                                    variant="secondary"
                                >
                                    {saveLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Search Overrides
                                </Button>
                            )}
                    </div>

                        {/* Table */}
                        {userListTemp.length > 0 ? (
                            <div className="border rounded-lg max-h-[calc(90vh-20rem)] overflow-auto mx-4 sm:mx-6">
                                <Table>
                                    <TableHeader className="sticky top-0 bg-card z-10 border-b">
                                        <TableRow>
                                            <TableHead>Group Number</TableHead>
                                            <TableHead>User Type</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>
                                                <div className="flex items-center gap-1">
                                                    Commission
                                                    <TooltipProvider>
                                                        <Tooltip delayDuration={0}>
                                                            <TooltipTrigger asChild>
                                                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-xs">
                                                                <p>Includes Override Commission</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </TableHead>
                                            <TableHead>Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {userListTemp.map((row, index) => (
                                            <TableRow key={`${row.groupNumber}-${index}`}>
                                                <TableCell className="font-medium">{row.groupNumber}</TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary">{row.userType}</Badge>
                                                </TableCell>
                                                <TableCell>{row.distributorName}</TableCell>
                                                <TableCell>{row.commissionPayable}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1 flex-wrap">
                                                        {!row.isAssigned ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 relative group"
                                                                onClick={() => handleAssignClick(row.groupNumber, row.topCommission, true)}
                                                            >
                                                                <User className="h-4 w-4" />
                                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                    Assign
                                                                </span>
                                                            </Button>
                                                        ) : (
                                                            <>
                                                                {!(row.userType === 'Web Campaign' && row.emailId === userdata?.email) && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 relative group"
                                                                        onClick={() => row.userType === 'Web Campaign' 
                                                                            ? handleEditCampaignClick(row.groupNumber, row.topCommission, row.userType, row.emailId, false)
                                                                            : handleEditGroupClick(row.groupNumber, row.topCommission, row.userType, false)
                                                                        }
                                                                    >
                                                                        <Pencil className="h-4 w-4" />
                                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                            Edit
                                                                        </span>
                                                                    </Button>
                                                                )}

                                                                {!(row.userType === 'Web Campaign' && row.emailId === userdata?.email) && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 relative group"
                                                                        onClick={() => userdata?.wholesaler_Id === 61 
                                                                            ? handleSpecialCase() 
                                                                            : handleResetGroupClick(row.groupNumber, row.topCommission)
                                                                        }
                                                                    >
                                                                        <RotateCcw className="h-4 w-4" />
                                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                            Reset
                                                                        </span>
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}

                                                        {row.isAssigned && row.isGlicRx && row.deepLink && (
                                                            <Tooltip open={copyTooltipOpen[index]} onOpenChange={(open) => setCopyTooltipOpen(prev => ({ ...prev, [index]: open }))} delayDuration={200}>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8"
                                                                        onClick={() => handleCopyDeeplink(row.deepLink, index)}
                                                                    >
                                                                        <Copy className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {copyTooltip[index] || 'Deeplink-Click to Copy'}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}

                                                        {row.userType !== 'Broker' && row.isAssigned && row.isGlicRx && row.deepLink && (
                                                            <Tooltip delayDuration={200}>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8"
                                                                        onClick={() => handleResendEmail(row.emailId, row.groupNumber, index)}
                                                                        disabled={loadingStates[`resend_${index}`]}
                                                                    >
                                                                        {loadingStates[`resend_${index}`] ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : (
                                                                            <Send className="h-4 w-4" />
                                                                        )}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {resendMessage}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}

                                                        {row.userType === 'Broker' && row.portalLink && (
                                                            <Tooltip open={copyTooltipPortalOpen[index]} onOpenChange={(open) => setCopyTooltipPortalOpen(prev => ({ ...prev, [index]: open }))} delayDuration={200}>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8"
                                                                        onClick={() => handleCopyPortalLink(row.portalLink, index)}
                                                                    >
                                                                        <Globe className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {copyTooltipPortal[index] || 'Enrollment Center - Click to Copy'}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}

                                                        {row.userType === 'Broker' && row.isAssigned && row.isGlicRx && row.deepLink && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8 relative group"
                                                                onClick={() => handleResendEmailBroker(row.emailId, row.groupNumber, index)}
                                                                disabled={loadingStates[`resendBroker_${index}`]}
                                                            >
                                                                {loadingStates[`resendBroker_${index}`] ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Send className="h-4 w-4" />
                                                                )}
                                                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 bg-primary text-primary-foreground">
                                                                    {resendMessageBroker}
                                                                </span>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                                                    </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                {dataNotFound || 'No records found'}
                                </div>
                        )}

                        {/* Pagination */}
                        {userList.length > 10 && (
                            <div className="flex justify-center mt-4 px-4 sm:px-6">
                                <Pagination
                                    activePage={activePage}
                                    itemsCountPerPage={10}
                                    totalItemsCount={userList.length}
                                    pageRangeDisplayed={5}
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="text-center mt-2 px-4 sm:px-6">
                                <p className={`text-sm ${error.toLowerCase().includes('success') ? 'text-green-600' : 'text-destructive'}`}>
                                    {error}
                                </p>
                            </div>
                        )}
                    </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Request Group Number Popup */}
            {toggleRequestGroupPopup && (
                <RequestGroupNumber 
                    text="Request Group Number" 
                    closePopup={toggleRequestGroup} 
                />
            )}
        </TooltipProvider>
    );
}

export default ManageGroupNumber;
